import { Database, Sun, Moon } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useTheme } from "@/lib/ThemeContext";
import { useAuth } from "@/lib/AuthContext";

export default function Navbar({ onOpenAuth, onOpenDashboard }) {
  const { theme, toggleTheme } = useTheme();
  const { user, logout } = useAuth();

  const getInitials = () => {
    if (user?.name) {
      const parts = user.name.trim().split(/\s+/);
      if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      return parts[0].substring(0, 2).toUpperCase();
    }
    return user?.email?.[0]?.toUpperCase() || "?";
  };

  const creditColor = user?.is_paid
    ? user.credits_balance_cents > 100
      ? "#10b981"
      : user.credits_balance_cents >= 1
        ? "#eab308"
        : "#ef4444"
    : "#10b981";

  return (
    <nav
      data-testid="navbar"
      className="sticky top-0 z-50 flex items-center justify-between px-6 py-3 backdrop-blur-xl"
      style={{
        background: "var(--sdb-glass)",
        borderBottom: "1px solid var(--sdb-border)",
      }}
    >
      <div className="flex items-center gap-3">
        <div
          data-testid="nav-logo"
          className="w-8 h-8 rounded-sm gradient-bg flex items-center justify-center"
        >
          <Database className="w-4 h-4 text-black" strokeWidth={2.5} />
        </div>
        <span className="font-semibold text-[15px] tracking-tight" style={{ color: "var(--sdb-text)" }}>
          MockDB
        </span>
        <Badge
          variant="outline"
          className="font-mono text-[9px] tracking-widest rounded-none px-2 py-0.5"
          style={{ borderColor: "var(--sdb-border-active)", color: "var(--sdb-text-muted)" }}
        >
          BETA
        </Badge>
      </div>

      <div className="flex items-center gap-3">
        {/* Theme toggle */}
        <button
          data-testid="theme-toggle"
          onClick={toggleTheme}
          className={`theme-toggle ${theme}`}
          title={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
        >
          <div className="theme-toggle-knob">
            {theme === "dark" ? (
              <Moon className="w-3 h-3 text-black" strokeWidth={2} />
            ) : (
              <Sun className="w-3 h-3 text-black" strokeWidth={2} />
            )}
          </div>
        </button>

        {user ? (
          <div className="flex items-center gap-2">
            {/* Avatar button — opens dashboard */}
            <button
              data-testid="nav-avatar"
              onClick={onOpenDashboard}
              className="flex items-center gap-2 rounded-sm px-2 py-1 transition-colors hover:bg-[var(--sdb-surface)]"
            >
              <div
                className="relative flex h-6 w-6 items-center justify-center rounded-sm overflow-hidden shrink-0"
              >
                <div className="absolute inset-0" style={{ backgroundColor: "var(--sdb-accent)", opacity: 0.15 }} />
                <span className="relative text-[10px] font-semibold" style={{ color: "var(--sdb-accent)" }}>
                  {getInitials()}
                </span>
              </div>
              <span className="font-mono text-[10px] max-w-[120px] truncate" style={{ color: "var(--sdb-text-muted)" }}>
                {user.email}
              </span>
            </button>

            {/* Credit balance badge */}
            {user.is_paid ? (
              <Badge
                variant="outline"
                className="font-mono text-[9px] rounded-none px-2 py-0.5"
                style={{ borderColor: creditColor, color: creditColor }}
              >
                ${(user.credits_balance_cents / 100).toFixed(2)}
              </Badge>
            ) : (
              <Badge
                variant="outline"
                className="font-mono text-[9px] rounded-none px-2 py-0.5"
                style={{ borderColor: "#10b981", color: "#10b981" }}
              >
                {3 - (user.free_generations_used || 0)} free left
              </Badge>
            )}

            <Button onClick={logout} variant="ghost" className="font-mono text-[10px] rounded-sm px-2 py-1.5 h-auto"
              style={{ color: "var(--sdb-text-muted)" }}>
              Logout
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Button
              onClick={onOpenAuth}
              variant="outline"
              className="font-mono text-xs rounded-sm px-4 py-2 h-auto"
              style={{ borderColor: "var(--sdb-border)", color: "var(--sdb-text-secondary)" }}
            >
              Sign In
            </Button>
            <Button
              onClick={onOpenAuth}
              className="gradient-bg text-black font-semibold text-xs rounded-sm px-4 py-2 h-auto glow-btn hover:text-black"
            >
              Sign Up — Free
            </Button>
          </div>
        )}
      </div>
    </nav>
  );
}
